import axios from "axios";
import { ZodError } from "zod";

export const CONTRACT_VIOLATION_USER_MESSAGE =
  "Server returned an unexpected response. Please try again or contact support.";

export const OFFLINE_USER_MESSAGE = "No internet connection. Check your network and try again.";

// Message-only error thrown from services, carrying the HTTP status the backend
// returned so callers can classify the failure. Extends Error, so existing
// `.message` consumers are unaffected.
export class ApiError extends Error {
  // The backend's HTTP status, or undefined for a no-response failure (network
  // error, timeout, backend down — an axios error with no `response`, or a
  // non-axios cause we can't classify).
  readonly status?: number;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = axios.isAxiosError(cause) ? cause.response?.status : undefined;
  }

  // The backend answered (any status), as opposed to being unreachable.
  get backendResponded(): boolean {
    return this.status !== undefined;
  }

  // A 5xx server fault: the request wasn't rejected for a client-side reason, so
  // re-syncing state won't help — callers should just surface the message.
  get isServerError(): boolean {
    return this.status !== undefined && this.status >= 500;
  }

  // The rejection suggests the client's view of server state is stale (e.g. a
  // record changed or removed in another session), so callers should refetch
  // before the user retries. Any 4xx except 401/403: those are auth failures —
  // the global 401 handler logs the user out, and a refetch would only repeat
  // the failure with the same token. Excluded as well: 5xx (a server fault) and
  // no response (the refetch would fail too).
  // The status contract is pending backend agreement (PR 594). If the backend
  // confirms 404/409 for "gone/changed" and 400/422 for validation, narrow this
  // to 404/409 so a validation rejection keeps the user's selection.
  get shouldResync(): boolean {
    return (
      this.backendResponded && !this.isServerError && this.status !== 401 && this.status !== 403
    );
  }
}

export const getApiResponseErrorMsg = (e: unknown, context?: string): string => {
  let message = "Something went wrong, please try again later.";

  if (e instanceof ZodError) {
    console.error(`[Contract violation${context ? ` in ${context}` : ""}]`, e.issues);
    return CONTRACT_VIOLATION_USER_MESSAGE;
  }

  /**
   * A request that produced no response at all, made while the browser reports
   * no connection. Axios surfaces this as the bare string "Network Error",
   * which reads to a user as though the server broke — this says whose problem
   * it actually is, and what to do about it.
   *
   * Both halves of the condition matter: `!e.response` alone also covers
   * timeouts and CORS failures on a perfectly good connection, which are not
   * the user's network and should keep their existing message.
   */
  if (
    axios.isAxiosError(e) &&
    !e.response &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  ) {
    return OFFLINE_USER_MESSAGE;
  }

  if (axios.isAxiosError(e) && e.response) {
    message = `${e.response?.data?.message || e.response?.statusText || message}`;
  } else if (e instanceof Error) {
    message = e.message;
  }

  return message;
};
